import { useMemo, useCallback } from "react";
import {
  normalizeValue,
  assigneeRefToEntityItem,
  selectionToUserEntity,
} from "./utils";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  Box,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelected,
  EntitySelectorSelectedChips,
  EntitySelectorEmpty,
  type EntitySelectorConfig,
  type EntitySelectorContentSize,
  type EntitySelectorVariant,
  type EntitySelection,
  type EntityItemConfig,
  type IconName,
} from "@salestrak/ui";
import { useUserPool } from "./hooks";
import type { EntityRefValue } from "#app/lib/data";
import { useEntityDetailOptional } from "#app/components/entity";
import { toEntityTriggerSize, type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

export type AssigneeSelectorProps = Omittable<{
  /** Currently selected member IDs */
  value?: string | string[] | null;
  /** Called when selection changes with an array of entity IDs */
  onValueChange?: (ids: string[]) => void;
  /** Seed selected assignees so current values render even if absent from the pool query */
  selectedRefs?: EntityRefValue[];
  /** Entity IDs to hide from the pool */
  exclude?: string[];
  /** Restrict the pool to active members only (excludes invited/pending). */
  activeOnly?: boolean;
  /** Maximum number of selections */
  maxSelections?: number;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  /** `"chips"` (default) pins removable chips above the pool; `"list"` uses a vertical selected section. */
  mode?: "chips" | "list";
  /** Whether selected assignees can be reordered to set the primary assignee. */
  allowSetPrimary?: boolean;
  /** Whether to show the Set primary action for every selected assignee. */
  showSetPrimaryOnAll?: boolean;
  /** Whether the selected primary assignee should show "Primary" subtext. */
  showPrimarySubtext?: boolean;
  /** Called when "Set primary" is clicked. Fires the mutation directly without reordering. */
  onSetPrimary?: (entityId: string) => void;
  /** When provided, called instead of opening the internal profile dialog. Use when the selector is rendered inside a popover that would unmount the dialog. */
  onViewProfile?: (userId: string) => void;
  placeholder?: string;
  size?: SelectorSize;
  disabled?: boolean;
  /** Render selections as removable chips in the trigger. */
  chips?: boolean;
  /** Leading icon shown in the trigger. */
  icon?: IconName;
  /** Stretch trigger to fill its container. */
  fill?: boolean;
  /** Match popover width to trigger. */
  contentFit?: boolean;
  /** Popover min-width preset. */
  contentSize?: EntitySelectorContentSize;
  children?: React.ReactNode;
}>;

/**
 * Domain wrapper for selecting issue assignees (members only).
 * Single-pool selector for members, with a guaranteed-but-changeable primary.
 */
export function AssigneeSelector({
  value,
  onValueChange,
  selectedRefs,
  exclude,
  activeOnly,
  maxSelections,
  variant,
  mode = "chips",
  allowSetPrimary = true,
  showSetPrimaryOnAll = false,
  showPrimarySubtext = true,
  onSetPrimary,
  onViewProfile,
  placeholder = "Select assignee...",
  size,
  disabled,
  chips,
  icon,
  fill,
  contentFit,
  contentSize,
  children,
}: AssigneeSelectorProps) {
  const { pool: userPool } = useUserPool({ exclude, activeOnly });
  const detail = useEntityDetailOptional();

  const mergedUserPool = useMemo(() => {
    const basePool = {
      ...userPool,
      itemBadge: { label: "Profile" },
    };

    if (!selectedRefs?.length) return basePool;

    const itemsById = new Map(basePool.items.map((item) => [item.id, item]));
    for (const ref of selectedRefs) {
      if (!itemsById.has(ref.entityId)) {
        itemsById.set(ref.entityId, {
          ...assigneeRefToEntityItem(ref),
          description:
            showPrimarySubtext && ref.isPrimary ? "Primary" : undefined,
        });
      }
    }

    return {
      ...basePool,
      items: Array.from(itemsById.values()),
      total: Math.max(basePool.total ?? 0, itemsById.size),
    };
  }, [selectedRefs, showPrimarySubtext, userPool]);

  const handleViewProfile = useCallback((entity: EntityItemConfig) => {
    if (onViewProfile) {
      onViewProfile(entity.id);
      return;
    }
    detail?.openEntity("user", entity.id);
  }, [onViewProfile, detail]);

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [mergedUserPool],
      maxSelections,
      searchPlaceholder: "Search members...",
      emptyMessage: "No results found",
      noItemsMessage: "No members yet",
    }),
    [mergedUserPool, maxSelections],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    const ids = normalizeValue(value);
    if (ids.length === 0) return [];
    const selectedRefMap = new Map(
      (selectedRefs ?? []).map((ref) => [ref.entityId, ref]),
    );

    const itemMap = new Map<string, { entity: EntitySelection["entity"]; poolId: string }>();
    for (const item of mergedUserPool.items) {
      itemMap.set(item.id, { entity: item, poolId: mergedUserPool.id });
    }

    return ids
      .map((id) => {
        const match = itemMap.get(id);
        if (!match) return null;
        const selectedRef = selectedRefMap.get(id);
        return {
          id,
          poolId: match.poolId,
          entity: selectedRef
            ? {
                ...match.entity,
                description:
                  showPrimarySubtext && selectedRef.isPrimary
                    ? "Primary"
                    : undefined,
                data: {
                  ...match.entity.data,
                  isPrimary: selectedRef.isPrimary ?? false,
                },
              }
            : match.entity,
        };
      })
      .filter(Boolean) as EntitySelection[];
  }, [value, mergedUserPool, selectedRefs, showPrimarySubtext]);

  const getSetPrimaryLabel = useCallback(
    (selection: EntitySelection) => {
      const index = selections.findIndex((item) => item.id === selection.id);
      if (index < 0) return undefined;
      return showSetPrimaryOnAll || index > 0 ? "Set primary" : undefined;
    },
    [selections, showSetPrimaryOnAll],
  );

  const handleSetPrimary = useCallback(
    (entity: EntityItemConfig) => {
      if (onSetPrimary) {
        onSetPrimary(entity.id);
        return;
      }

      const index = selections.findIndex((selection) => selection.id === entity.id);
      if (index <= 0) return;

      const nextSelections = [
        at(selections, index, "selection"),
        ...selections.slice(0, index),
        ...selections.slice(index + 1),
      ];

      onValueChange?.(nextSelections.map((selection) => selection.id));
    },
    [onSetPrimary, onValueChange, selections],
  );

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      onValueChange?.(next.map((s) => s.id));
    },
    [onValueChange],
  );

  return (
    <>
      <EntitySelector
        variant={variant}
        config={config}
        value={selections}
        onValueChange={handleValueChange}
      >
        {children ?? (
          <>
            {chips ? (
              <EntitySelectorTrigger
                placeholder={placeholder}
                size={toEntityTriggerSize(size)}
                disabled={disabled}
                chips
                fill={fill}
                icon={icon}
              />
            ) : (
              <SelectionEntityTrigger
                toEntity={selectionToUserEntity}
                placeholder={placeholder}
                title="Assignees"
                emptyIcon={icon}
                size={size}
                disabled={disabled}
                fill={fill}
                // Route through the caller's handler so it doesn't open the
                // global dialog, which would unmount a transient popover.
                onViewProfile={
                  onViewProfile ? (_kind, id) => onViewProfile(id) : undefined
                }
              />
            )}
            {/* Row actions are wired per list (pool/selected), not on the
                content shell — the drawer variant renders the same children. */}
            <EntitySelectorContent size={contentSize} fit={contentFit}>
              {mode === "list" ? (
                <Box direction="column" overflow="hidden" height={400}>
                  <EntitySelectorInput />
                  <EntitySelectorSelected
                    label="Assignees"
                    hideWhenEmpty
                    hideBadge
                    hideSelectionHighlight
                    maxHeight={160}
                    onItemAction={handleViewProfile}
                    getItemSecondaryActionLabel={
                      allowSetPrimary ? getSetPrimaryLabel : undefined
                    }
                    onItemSecondaryAction={
                      allowSetPrimary ? handleSetPrimary : undefined
                    }
                  />
                  <EntitySelectorPool
                    value={mergedUserPool.id}
                    hideSelectionHighlight
                    onItemAction={handleViewProfile}
                  />
                  <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
                </Box>
              ) : (
                <Box direction="column" overflow="hidden" height={400}>
                  <EntitySelectorInput />
                  <EntitySelectorSelectedChips label="Selected" />
                  <EntitySelectorPool
                    value={mergedUserPool.id}
                    onItemAction={handleViewProfile}
                  />
                  <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
                </Box>
              )}
            </EntitySelectorContent>
          </>
        )}
      </EntitySelector>
    </>
  );
}
